import { PaginatorInfo } from "./generic.dto";

/** Any JSON value, as stored in the audit service's JSON columns */
export type AuditLogJson =
  | string
  | number
  | boolean
  | null
  | AuditLogJson[]
  | { [key: string]: AuditLogJson };

/** The user who performed the audited action */
export type AuditLogActor = {
  userId: number | string
  username: string
  name: string
  category: string
  employeeId?: number | null
  platformUser: boolean
}

export enum AuditLogOutcome {
  SUCCESSFUL = 'SUCCESSFUL',
  FAILED = 'FAILED',
}

/**
 * Error envelope recorded for a failed request. Usually `{ error, message }`;
 * access denials also carry `method`/`path`, some failures a `cause`.
 */
export type AuditLogError = {
  error?: string
  message?: string
  method?: string
  path?: string
  cause?: AuditLogJson
  [key: string]: AuditLogJson | undefined
}

/**
 * Record snapshots keyed like "Bonus#3" or
 * "EmployeeLeaveTypeSummary#employeeId=1,leaveTypeId=2,year=2026", each
 * mapping to a redacted record. May carry `_truncated: true`, or be
 * `{ _omitted: "<reason>" }` instead.
 */
export type AuditLogSnapshot = { [key: string]: AuditLogJson };

/** The audit service's copy of the organization; null until it has synced it */
export type AuditLogOrganization = {
    id: string
    name: string
    status: string
    [key: string]: AuditLogJson | undefined
}

/** The audit service's copy of the payroll company; null until it has synced it */
export type AuditLogCompany = {
    id: number
    organizationId: string
    name: string
    status: string
    [key: string]: AuditLogJson | undefined
}

type AuditLog = {
    id: number
    action: string
    organizationId: string
    requestId: string
    user: AuditLogActor
    input: AuditLogJson | null
    companyId: number | null
    /** Gateway identifier of the producing service, e.g. "user" */
    service: string | null
    outcome: AuditLogOutcome
    statusCode: number | null
    error: AuditLogError | null
    dataBefore: AuditLogSnapshot | null
    dataAfter: AuditLogSnapshot | null
    /** Internal to the audit service */
    sourceOffset?: string | number | null
    timestamp: string
    modifiedAt: string | null
    organization?: AuditLogOrganization | null
    company?: AuditLogCompany | null
}

export enum AuditLogOrderBy {
  TIMESTAMP_ASC = 'timestamp:asc',
  TIMESTAMP_DESC = 'timestamp:desc',
}

export class QueryAuditLogDto {
  action?: string;
  organizationId?: string;
  companyId?: number;
  outcome?: AuditLogOutcome;
  service?: string;
  /** "YYYY-MM-DD HH:mm:ss" in UTC; the table filter's dates are converted in audit.api */
  'timestamp.gte'?: string;
  'timestamp.lte'?: string;
  page = 1;
  limit?: number;
  orderBy: AuditLogOrderBy = AuditLogOrderBy.TIMESTAMP_DESC;
}

/** The search endpoint only takes `q`; it ignores every other filter */
export class SearchAuditLogDto {
  q?: string;

  page = 1;
  limit?: number;
  orderBy: AuditLogOrderBy = AuditLogOrderBy.TIMESTAMP_DESC;
}

// The list, get and search responses include `organization` and `company`
export type AuditLogDto = AuditLog;
export interface AuditLogPaginator extends PaginatorInfo<AuditLogDto> {}
